/**
 * SchemaFormFragment - A React component for rendering schema-based form fragments
 *
 * This component is part of a schema-driven form system that dynamically renders
 * form elements based on JSON schema definitions. It supports various form element
 * types including strings, numbers, booleans, arrays, objects, dates, and more.
 *
 * A schema-based form (SchemaForm) consists of a tree of fragments, which is what
 * you find here. "Leaf" fragments would correspond to eg. simple text inputs,
 * checkboxes or static text, while the root fragment of a form would probably
 * consist of multiple nested fragments, as it usually corresponds to an object.
 *
 * Features:
 * - Dynamic form rendering based on schema definitions
 * - Support for nested objects and arrays
 * - Validation error display
 * - Read-only mode support
 * - Default value handling
 * - Additional properties support for objects
 * - Pattern-based property validation
 *
 * @returns a react component, or throws an error if rendering is not
 *          possible (eg. invalid schema)
 */

import { Accordion } from "components/Accordion/Accordion";
import { Button } from "components/Button/Button";
import { Autocomplete } from "components/Form/components/Autocomplete/Autocomplete";
import { Callout } from "components/Form/components/Callout/Callout";
import { ColorPicker } from "components/Form/components/ColorPicker/ColorPicker";
import { DurationField } from "components/Form/components/Duration/Duration";
import { EditableMarkdownComponent } from "components/Form/components/EditableMarkdown/EditableMarkdown";
import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import { Number } from "components/Form/components/Number/Number";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import { ProgressBarSchemaFormComponent } from "components/Form/components/SchemaForm/components/ProgressBarSchemaFormComponent";
import { LazyResizeableCodeEditor as ResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { ArrayDragAndDrop } from "components/Form/components/SchemaForm/ArrayDragAndDrop";
import { useSchemaFormArrayItemIds } from "components/Form/components/SchemaForm/SchemaFormArrayItemIds";
import { useSchemaFormRecord } from "components/Form/components/SchemaForm/SchemaFormRecordContext";
import {
    arrayItemCollapseKey,
    collapseStorageKey,
    useRememberedCollapse,
} from "components/Form/components/SchemaForm/rememberedCollapse";
import { LabelledFormElement } from "components/Form/components/SchemaForm/LabelledFormElement";
import { IncomingReference } from "components/Form/components/SchemaForm/IncomingReference";
import { RecordFilterBuilder } from "components/Form/components/RecordFilterBuilder/RecordFilterBuilderFormComponent";
import {
    ObjectPath,
    SchemaFormErrorData,
    SchemaFormSchema,
} from "components/Form/components/SchemaForm/SchemaForm.types";
import { SchemaFormRenderErrors } from "components/Form/components/SchemaForm/SchemaForm.constants";
import { SchemaFormObjectKeyPopover } from "components/Form/components/SchemaForm/SchemaFormObjectKeyPopover";
import {
    ADDITIONAL_PROPERTIES,
    EMPTY_OBJECT_PATH,
    extendObjectPath,
    fieldContainerClassName,
    getAdditionalPropertyLabel,
    getByPath,
    getDefaultRequiredValue,
    getIsOptional,
    getShowsRequiredMark,
    getSchemaFormErrorMessage,
    getUpdatedData,
    isErrorPathWithin,
    jsonFieldSchema,
} from "components/Form/components/SchemaForm/SchemaFormService";
import { ClearsOnChangeLock } from "components/Form/components/SchemaForm/components/ClearsOnChangeLock";
import { DatePickerSchemaFormComponent } from "components/Form/components/SchemaForm/components/DatePickerSchemaFormComponent";
import { PropertyKeyField } from "components/Form/components/SchemaForm/components/SchemaFormPropertKeyField";
import {
    SchemaFormActionButtons,
    unsetValue,
} from "components/Form/components/SchemaForm/components/components";
import { SchemaFormDropdown } from "components/Form/components/SchemaForm/fragments/SchemaFormDropdown";
import { SchemaFormReadonlyReference } from "components/Form/components/SchemaForm/fragments/SchemaFormReadonlyReference";
import {
    getRenderableHiddenEntries,
    getShownAndHiddenProperties,
} from "components/Form/components/SchemaForm/utils.ts/getShownAndHiddenProperties";
import {
    TriStateCircularSwitch,
    TriStateSwitch,
} from "components/Form/components/Switch/TriStateSwitch";
import { getNextTriStateValue } from "components/Form/components/Switch/getNextTriStateValue";
import { Text } from "components/Form/components/Text/Text";
import Tooltip from "components/Tooltip/Tooltip";
import customObjectConfig from "config/resources/custom_object";
import { getDocumentationUrl } from "containers/Documentation/documentationService";
import { isObject } from "lodash";
import { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { getSearchObj } from "utils/utils";
import {
    parseStructuredData,
    serializeStructuredData,
    serializeToPrettyYaml,
} from "utils/valueTransformers";
import { useStructuredDataDisplayMode } from "utils/structuredDataDisplayMode";

/**
 * Collapsible "More..." section holding an object's hidden (`is_hidden`)
 * properties. Renders as a native <details> that stays collapsed by default
 * (unchanged behaviour), but auto-expands and scrolls the offending field into
 * view when `hasError` is true — i.e. the current validation error points into
 * one of the hidden fields. Without this, a hidden invalid field (e.g. an
 * invalid `runtime` value) disables Save with no visible reason
 * (operator-task-fe-save-disabled-no-reason-when-hidden-attribute-invalid).
 * The user can still toggle the section manually via the native summary.
 */
const MoreSection = ({ hasError, children }: { hasError: boolean; children: ReactNode }) => {
    const [isOpen, setIsOpen] = useState(hasError);
    const detailsRef = useRef<HTMLDetailsElement>(null);

    // Force the section open whenever a blocking error appears inside it. We do
    // NOT force it closed again, so a user who manually collapses it is respected.
    useEffect(() => {
        if (hasError) {
            setIsOpen(true);
        }
    }, [hasError]);

    // Once the section is open because of an error, bring the first invalid field
    // into view. `.errorFieldContainer` is the class the field fragment already
    // applies to an invalid field's container (fieldContainerClassName).
    useEffect(() => {
        if (hasError && isOpen && detailsRef.current) {
            const target =
                detailsRef.current.querySelector(".errorFieldContainer") ?? detailsRef.current;
            target.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [hasError, isOpen]);

    return (
        <details
            ref={detailsRef}
            open={isOpen}
            onToggle={(event) => setIsOpen((event.target as HTMLDetailsElement).open)}
        >
            <summary>
                <h4>More...</h4>
            </summary>
            <div className="content">{children}</div>
        </details>
    );
};

export interface SchemaFormFragmentProps {
    schema: SchemaFormSchema;
    schemaPath: ObjectPath;
    formData: Object;
    dataPath: ObjectPath;
    setData: (data: any) => void;
    defaultOverridePath?: ObjectPath;
    isRoot: boolean;
    isReadOnly: boolean;
    showLabel?: boolean;
    isRequired?: boolean;
    // Schema-required-ness used ONLY for the visual required cue (the asterisk and
    // keeping a required-but-unset field from being grayed-out / "removed"),
    // INDEPENDENT of `omitRequired`. `omitRequired` legitimately suppresses required
    // *validation* on resource value forms (a connector's stored `value` may omit
    // required fields, which are supplied at use-time) — but a required field must
    // still LOOK required so users don't overlook it (operator-task-schema-form-
    // required-no-default-field-grayed-out-overlooked). Falls back to `isRequired`
    // when not provided, so forms that don't set `omitRequired` are unchanged.
    isStyleRequired?: boolean;
    isRecordDisabled?: boolean;
    // Suppress the per-field action buttons (unset / reset-to-default / remove)
    // independently of `isRecordDisabled`. Needed by compact inline list-editing
    // cells that render an INTERACTIVE editor (isRecordDisabled=false so a
    // form-string cell is editable rather than read-only) but must not clutter
    // the cell with action buttons. Defaults to the existing behaviour.
    hideActionButtons?: boolean;
    errorData?: SchemaFormErrorData;
    isNewRecord?: boolean;
    ajv?: any;
    additionalCodeEditorRootProps?: {
        initialHeight?: number;
        minHeight?: number;
    };
    omitRequired?: boolean;
    // Optional submit sink for the schema-form-dialect `form-submit` action-button
    // element. When present (e.g. an interactive message form) clicking a
    // `form-submit` leaf writes {<propertyName>: true} into the form data and calls
    // this with the property NAME (= the chosen action identifier), so the
    // surrounding chrome can submit the response and record which button was pressed.
    // Absent on ordinary data forms, where a `form-submit` button renders inert
    // (disabled), since there is no submit sink to dispatch to.
    onSubmitAction?: (actionName: string) => void;
    // Name of the `form-submit` action whose submit is in flight (an interactive
    // message form waiting for its response to be stored and the execution to
    // continue). That button shows its loading state and every action button is
    // disabled until the caller clears it.
    pendingSubmitAction?: string | null;
}

const SchemaFormFragmentWithError = ({
    schema,
    schemaPath,
    formData,
    dataPath,
    setData,
    defaultOverridePath,
    isRoot,
    isReadOnly,
    showLabel = true,
    isRequired,
    isStyleRequired,
    isRecordDisabled,
    hideActionButtons,
    errorData,
    isNewRecord,
    ajv,
    additionalCodeEditorRootProps = {},
    omitRequired,
    onSubmitAction,
    pendingSubmitAction,
}: SchemaFormFragmentProps) => {
    if (schema === null || schema === undefined) {
        throw SchemaFormRenderErrors.invalidSchema;
    }

    // Drive the required *styling* (asterisk + never-grayed-out) by schema-required-
    // ness even when required *validation* is omitted (omitRequired on resource value
    // forms), so a required field never reads as optional. Falls back to isRequired.
    const styleRequired = isStyleRequired ?? isRequired;

    // fetch the schema of the element under "schemaPath"
    const elementSchema = useMemo(
        () =>
            getByPath({
                data: schema,
                dataPath: schemaPath,
                schema,
                elementSchema: schema,
            }),
        [schema, schemaPath],
    );

    /**
     * Determines if the current form element can be reset to its default value.
     * For form-objects, this depends on whether most properties are const values.
     * For other element types, it's always allowed.
     */
    const getIsSetToDefaultAllowed = useMemo(() => {
        // If it is not a form-object element it is writable
        if (!(
            elementSchema.element === "form-object" ||
            elementSchema.element === "form-optional-object"
        )) {
            return true;
            // If there are no properties, it is not writable
        } else if (
            !elementSchema.properties ||
            Object.keys(elementSchema.properties).length === 0
        ) {
            return false;
        }
        // Count how many properties include "const"
        const properties = Object.values(elementSchema.properties);
        const constCount = properties.filter((property) => {
            const propertyWithType = property as { element: string };
            return propertyWithType.element ? propertyWithType.element.includes("const") : false;
        }).length;

        // False if at most one doesn't include "const"
        return !(constCount >= properties.length - 1);
    }, [elementSchema.element, elementSchema.properties]);

    /**
     * Determines whether this form fragment should be rendered.
     * Renders if it has additional properties, or if it has regular properties
     * that aren't all const values without labels/descriptions.
     */
    const shouldRender = useCallback(
        (hasAdditionalProperties: boolean) => {
            // If it has additional properties, render
            if (hasAdditionalProperties) {
                return true;
            }
            // If there are no properties, don't render
            if (!elementSchema.properties || Object.keys(elementSchema.properties).length === 0) {
                return false;
            }
            // If all properties are const and they don't have label or description, don't render
            const allAreConstsWithLabelOrDescription = Object.values(
                elementSchema.properties,
            ).every((property) => {
                const propertyWithType = property as {
                    element: string;
                    label?: string;
                    description?: string;
                };
                const isConst = propertyWithType.element
                    ? propertyWithType.element.includes("const")
                    : false;
                const hasLabelOrDescription =
                    propertyWithType.label || propertyWithType.description;
                return isConst && !hasLabelOrDescription;
            });

            return !allAreConstsWithLabelOrDescription;
        },
        [elementSchema.properties],
    );

    if (elementSchema === null || elementSchema === undefined) {
        throw SchemaFormRenderErrors.noElementSchema;
    }

    const arrayItemIds = useSchemaFormArrayItemIds();
    // Opt-in (array schema `rememberCollapsed: true`): remember which items the
    // user collapsed, per record + array path, across remounts and reloads.
    const { recordId } = useSchemaFormRecord();
    const rememberedCollapse = useRememberedCollapse(
        elementSchema.rememberCollapsed && recordId ? collapseStorageKey(recordId, dataPath) : null,
    );
    // Keys of `clears_on_change` fields the user has unlocked (confirmed) here.
    const [unlockedKeys, setUnlockedKeys] = useState<string[]>([]);

    // fetch the data of the element under "dataPath"
    // use the element schema to fall back to default values
    const elementData = useMemo(
        () =>
            getByPath({
                data: formData,
                dataPath,
                schema,
                elementSchema,
                defaultOverridePath,
            }),
        [dataPath, defaultOverridePath, elementSchema, formData, schema],
    );

    const codeEditorValue = useRef<string | undefined>();
    const structuredDataMode = useStructuredDataDisplayMode();
    const serializedElementData = serializeToPrettyYaml(elementData);

    // Drop any in-flight editor text when form data changes externally (e.g. reset
    // to default) or when the JSON/YAML display mode is toggled — in both cases the
    // editor text is re-derived from the current data in the active representation,
    // so a stale buffer must not be parsed on blur.
    useEffect(() => {
        codeEditorValue.current = undefined;
    }, [serializedElementData, structuredDataMode]);

    const getRequired = useCallback(
        (requiredArray, key) => requiredArray?.includes(key) ?? false,
        [],
    );

    const defaultValue = useMemo(
        () =>
            elementSchema.default === undefined
                ? getDefaultRequiredValue(schema, elementSchema, null)
                : elementSchema.default,
        [elementSchema, schema],
    );

    const shownButtons = useMemo(() => {
        return {
            hasRemoveButton: elementSchema.style?.remove_button !== false,
            hasResetToDefaultButton: elementSchema.style?.reset_to_default_button !== false,
            hasClearButton: elementSchema.style?.clear_button !== false,
        };
    }, [elementSchema.style]);

    const getActionButtons = useCallback(
        (
            additionalShownButtons: {
                hasRemoveButton?: boolean;
                hasResetToDefaultButton?: boolean;
                hasClearButton?: boolean;
            } = {},
        ) =>
            isRecordDisabled || hideActionButtons ? null : (
                <SchemaFormActionButtons
                    value={elementData}
                    defaultValue={defaultValue}
                    elementType={elementSchema.element}
                    isRequired={isRequired}
                    unsetField={() => setData(getUpdatedData(formData, dataPath, unsetValue))}
                    resetToDefault={() => setData(getUpdatedData(formData, dataPath, defaultValue))}
                    removeField={() => setData(getUpdatedData(formData, dataPath, undefined))}
                    isOptional={getIsOptional(elementSchema.element)}
                    isSetToDefaultAllowed={getIsSetToDefaultAllowed}
                    shownButtons={{ ...shownButtons, ...additionalShownButtons }}
                />
            ),
        [
            isRecordDisabled,
            hideActionButtons,
            elementData,
            defaultValue,
            elementSchema.element,
            isRequired,
            getIsSetToDefaultAllowed,
            shownButtons,
            setData,
            formData,
            dataPath,
        ],
    );

    const actionButtons = useMemo(() => getActionButtons(), [getActionButtons]);

    const fieldValue = useMemo(
        () => (elementData === undefined ? elementSchema.default : elementData),
        [elementData, elementSchema.default],
    );
    const placeholder = useMemo(() => (fieldValue == null ? "..." : undefined), [fieldValue]);

    const errorMessage = useMemo(
        () => getSchemaFormErrorMessage(dataPath, errorData),
        [dataPath, errorData],
    );

    // A secret field with no value has nothing to hide: show its input directly
    // instead of the "Show" button, so an empty password (a new record, or a
    // credential just cleared by changing a destination field) reads as empty
    // and the user can see where to type. Sticky for this mount, so typing the
    // first character does not fold the input back behind the button.
    const isSecretInputShownRef = useRef(false);
    if (elementSchema.secret && (elementData == null || elementData === "")) {
        isSecretInputShownRef.current = true;
    }
    const isSecretMasked = Boolean(elementSchema.secret) && !isSecretInputShownRef.current;

    const labelMinimumProps = useMemo(
        () => ({
            label: showLabel ? elementSchema.label : "",
            description: showLabel ? elementSchema.description : "",
            labelClassName: `${
                showLabel && getShowsRequiredMark(elementSchema.element, styleRequired)
                    ? "isRequired"
                    : ""
            }`,
            errorMessage,
            elementSchemaStyle: elementSchema.style,
            secret: isSecretMasked,
        }),
        [
            elementSchema.description,
            elementSchema.element,
            elementSchema.label,
            isSecretMasked,
            elementSchema.style,
            errorMessage,
            styleRequired,
            showLabel,
        ],
    );

    const labelProps = useMemo(
        () => ({
            ...labelMinimumProps,
            // A required field is never rendered "removed" (grayed-out): an unset
            // required field must read as an active, mandatory field the user has to
            // fill, not as an optional one to overlook. Only optional-and-unset fields
            // keep the grayed-out "click to add" affordance.
            isRemoved: elementData === undefined && !styleRequired,
            onAdd: () =>
                !isRecordDisabled &&
                setData(getUpdatedData(formData, dataPath, defaultValue ?? null)),
        }),
        [
            dataPath,
            defaultValue,
            elementData,
            formData,
            isRecordDisabled,
            styleRequired,
            labelMinimumProps,
            setData,
        ],
    );

    /**
     * Finds a pattern property key that matches the given property key using regex.
     * @param propertyKey - The property key to match against patterns
     * @returns The matching pattern key or undefined
     */
    const findKeyByPatternProperty = useCallback(
        (propertyKey) => {
            if (elementSchema.patternProperties) {
                const foundKey = Object.keys(elementSchema.patternProperties).find((key) =>
                    propertyKey.match(key),
                );
                return foundKey;
            }
        },
        [elementSchema.patternProperties],
    );

    /**
     * Finds the schema for additional properties, falling back to JSON field schema if needed.
     * @returns The schema for additional properties or undefined
     */
    const findSchemaInAdditionalProperties = useCallback(() => {
        if (elementSchema.additionalProperties) {
            return elementSchema.additionalProperties === true
                ? jsonFieldSchema
                : elementSchema.additionalProperties;
        }
    }, [elementSchema.additionalProperties]);

    const memoizedElement = useMemo(() => {
        // A record filter renders as plain JSON when read-only (the builder is an editor).
        const renderedElement =
            isReadOnly &&
            (elementSchema.element === "form-record-filter" ||
                elementSchema.element === "form-optional-record-filter")
                ? "form-json"
                : elementSchema.element;
        switch (renderedElement) {
            case "ref":
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        <SchemaFormFragment
                            isRoot={false}
                            isReadOnly={isReadOnly}
                            isRecordDisabled={isRecordDisabled}
                            hideActionButtons={hideActionButtons}
                            schema={schema}
                            schemaPath={EMPTY_OBJECT_PATH} // reset schemaPath to access the root node of the schema
                            formData={formData}
                            dataPath={dataPath} // retain dataPath to access recursively nested data
                            setData={setData}
                            errorData={errorData}
                            isNewRecord={isNewRecord}
                            ajv={ajv}
                            omitRequired={omitRequired}
                        />
                    </LabelledFormElement>
                );

            case "form-dropdown":
            case "form-optional-dropdown":
                return (
                    <SchemaFormDropdown
                        elementSchema={elementSchema}
                        elementData={elementData}
                        schema={schema}
                        schemaPath={schemaPath}
                        formData={formData}
                        dataPath={dataPath}
                        setData={setData}
                        isRoot={isRoot}
                        isReadOnly={isReadOnly}
                        isRecordDisabled={isRecordDisabled}
                        showLabel={showLabel}
                        isRequired={isRequired}
                        isStyleRequired={styleRequired}
                        errorData={errorData}
                        ajv={ajv}
                        shownButtons={shownButtons}
                        omitRequired={omitRequired}
                    />
                );

            case "form-array":
            case "form-optional-array":
                const additionalItemLabel = (startsWithCap?: boolean) =>
                    getAdditionalPropertyLabel("item", elementSchema.itemsLabel, startsWithCap);
                const itemIds = Array.isArray(elementData)
                    ? arrayItemIds.getItemIds(dataPath, elementData.length)
                    : [];
                const getChild = (index: number, _itemId: string) => {
                    const collapseKey = arrayItemCollapseKey(
                        elementData[index],
                        index,
                        elementSchema.itemKeyProperty,
                    );
                    return (
                        <div className="formEditorField">
                            <Accordion
                                className="arrayItemAccordion"
                                title={`${additionalItemLabel(true)} ${index + 1}`}
                                onDelete={
                                    isReadOnly
                                        ? undefined
                                        : () => {
                                              const performDelete = () => {
                                                  arrayItemIds.removeItemId(dataPath, index);
                                                  const newValue = [...elementData];
                                                  newValue.splice(index, 1);
                                                  setData(
                                                      getUpdatedData(formData, dataPath, newValue),
                                                  );
                                              };
                                              // Opt-in (schema `confirmDelete: true`): a destructive
                                              // array-item delete asks for confirmation first, so a
                                              // single stray click on the item's 'x' cannot silently
                                              // discard it. Other arrays keep the immediate delete.
                                              if (elementSchema.confirmDelete) {
                                                  showConfirmModal({
                                                      title: `Delete ${additionalItemLabel(
                                                          true,
                                                      )} ${index + 1}?`,
                                                      content: `Are you sure you want to delete this ${additionalItemLabel()}?`,
                                                      confirmText: "Delete",
                                                      cancelText: "Cancel",
                                                      className: "confirmationModal",
                                                      confirmButtonProps: {
                                                          className: "button button--danger",
                                                      },
                                                      onConfirm: performDelete,
                                                  });
                                              } else {
                                                  performDelete();
                                              }
                                          }
                                }
                                isExpandedByDefault
                                {...(rememberedCollapse.isActive
                                    ? {
                                          isExpanded: !rememberedCollapse.isCollapsed(
                                              collapseKey,
                                          ),
                                          onExpandedChange: (expanded: boolean) =>
                                              rememberedCollapse.setCollapsed(
                                                  collapseKey,
                                                  !expanded,
                                              ),
                                      }
                                    : {})}
                            >
                                <div className="content">
                                    <SchemaFormFragment
                                        isRoot={false}
                                        isReadOnly={isReadOnly}
                                        isRecordDisabled={isRecordDisabled}
                                        hideActionButtons={hideActionButtons}
                                        schema={schema}
                                        schemaPath={extendObjectPath(schemaPath, "items")}
                                        formData={formData}
                                        dataPath={extendObjectPath(dataPath, String(index))}
                                        setData={setData}
                                        defaultOverridePath={extendObjectPath(
                                            schemaPath,
                                            "default",
                                            String(index),
                                        )}
                                        errorData={errorData}
                                        isNewRecord={isNewRecord}
                                        ajv={ajv}
                                        omitRequired={omitRequired}
                                    />
                                </div>
                            </Accordion>
                        </div>
                    );
                };
                return (
                    <LabelledFormElement type="array" actionButtons={actionButtons} {...labelProps}>
                        {Array.isArray(elementData) && (
                            <ArrayDragAndDrop
                                items={elementData}
                                itemIds={itemIds}
                                setNewData={(changedItems, reorderedIds) => {
                                    arrayItemIds.replaceItemIds(dataPath, reorderedIds);
                                    setData(getUpdatedData(formData, dataPath, changedItems));
                                }}
                                getChild={getChild}
                                canDrag={!isReadOnly && !isRecordDisabled}
                            />
                        )}
                        {isReadOnly ? null : (
                            <Button
                                onClick={() => {
                                    arrayItemIds.addItemId(dataPath);
                                    const newValue = [
                                        ...(elementData || []), // TODO: check: is `|| []` needed here?
                                        getDefaultRequiredValue(schema, elementSchema.items, null),
                                    ];
                                    setData(getUpdatedData(formData, dataPath, newValue));
                                }}
                                label={`+ Add ${additionalItemLabel()}`}
                                variation="white"
                                size="small"
                                disabled={isReadOnly}
                            />
                        )}
                    </LabelledFormElement>
                );

            case "form-object":
            case "form-optional-object":
                const additionalPropertyLabel = (startsWithCap?: boolean) =>
                    getAdditionalPropertyLabel(
                        "property",
                        elementSchema.additionalPropertiesLabel,
                        startsWithCap,
                    );
                const hasAdditionalProperties =
                    !elementSchema.properties?.defaultIndex &&
                    (elementSchema.additionalProperties ||
                        (elementSchema.patternProperties &&
                            isObject(elementSchema.patternProperties) &&
                            Object.keys(elementSchema.patternProperties).length > 0));

                /**
                 * Finds the appropriate schema for a property key in pattern properties or additional properties.
                 * Validates that the key doesn't already exist and matches required patterns.
                 * @param propertyKey - The key to find schema for
                 * @param setError - Function to set validation errors
                 * @returns The schema for the property or undefined if not found
                 */
                const findSchema = ({ propertyKey, setError }) => {
                    // check whether key exists
                    const keyExists = Object.keys(elementData ?? {}).includes(propertyKey);
                    if (keyExists) {
                        setError(`${additionalPropertyLabel(true)} already exists.`);
                        return;
                    }

                    let foundSchema: any = null;
                    if (elementSchema.patternProperties) {
                        const patternKey = findKeyByPatternProperty(propertyKey);
                        if (patternKey) {
                            foundSchema = elementSchema.patternProperties[patternKey];
                            return foundSchema;
                        }
                    }
                    if (elementSchema.additionalProperties) {
                        return findSchemaInAdditionalProperties();
                    } else {
                        const regexString = Object.keys(elementSchema.patternProperties).join(
                            " or ",
                        );
                        setError(
                            `${propertyKey} is not a valid choice. The new property must match the regex ${regexString}`,
                        );
                    }
                };
                const { shownProperties, hiddenProperties } = getShownAndHiddenProperties(
                    elementSchema,
                    elementData,
                );

                return shouldRender(hasAdditionalProperties) ? (
                    <LabelledFormElement
                        key={`SchemaForm.${elementSchema.label}.LabelledFormElement`}
                        type="object"
                        actionButtons={actionButtons}
                        isRoot={isRoot}
                        {...labelProps}
                    >
                        {/* render all non-hidden elements defined in properties */}
                        {elementSchema.properties &&
                            Object.entries(shownProperties).map(
                                ([key, value]: [key: string, value: any]) => {
                                    const isConstWithoutLabelOrDescription = value.element
                                        ? value.element.includes("const") &&
                                          !(value.label || value.description)
                                        : false;
                                    if (!isConstWithoutLabelOrDescription) {
                                        // `clears_on_change` (connector destination fields such as
                                        // host): on an existing record the field stays read-only
                                        // until the user confirms that changing it clears the listed
                                        // fields (the stored credential).
                                        const clearedKeys: string[] = (
                                            Array.isArray(value.clears_on_change)
                                                ? value.clears_on_change
                                                : []
                                        ).filter(
                                            (target: string) => elementData?.[target] !== undefined,
                                        );
                                        const isLocked =
                                            clearedKeys.length > 0 &&
                                            !isNewRecord &&
                                            !isReadOnly &&
                                            !isRecordDisabled &&
                                            !unlockedKeys.includes(key);
                                        const field = (
                                            <SchemaFormFragment
                                                isRoot={false}
                                                isReadOnly={isReadOnly || isLocked}
                                                isRecordDisabled={isRecordDisabled}
                                                hideActionButtons={hideActionButtons}
                                                schema={schema}
                                                schemaPath={extendObjectPath(
                                                    schemaPath,
                                                    "properties",
                                                    key,
                                                )}
                                                formData={formData}
                                                dataPath={extendObjectPath(dataPath, key)}
                                                setData={setData}
                                                isRequired={
                                                    !omitRequired &&
                                                    getRequired(elementSchema.required, key)
                                                }
                                                isStyleRequired={Boolean(
                                                    getRequired(elementSchema.required, key),
                                                )}
                                                errorData={errorData}
                                                isNewRecord={isNewRecord}
                                                ajv={ajv}
                                                omitRequired={omitRequired}
                                                onSubmitAction={onSubmitAction}
                                                pendingSubmitAction={pendingSubmitAction}
                                            />
                                        );
                                        return (
                                            <div key={key} className="formEditorField">
                                                {isLocked ? (
                                                    <ClearsOnChangeLock
                                                        label={value.label ?? key}
                                                        clearedLabels={clearedKeys.map(
                                                            (target) =>
                                                                elementSchema.properties?.[target]
                                                                    ?.label ?? target,
                                                        )}
                                                        onUnlock={() => {
                                                            let next = formData;
                                                            clearedKeys.forEach((target) => {
                                                                next = getUpdatedData(
                                                                    next,
                                                                    extendObjectPath(
                                                                        dataPath,
                                                                        target,
                                                                    ),
                                                                    getDefaultRequiredValue(
                                                                        schema,
                                                                        elementSchema.properties[
                                                                            target
                                                                        ],
                                                                        null,
                                                                    ),
                                                                );
                                                            });
                                                            setData(next);
                                                            setUnlockedKeys((keys) => [
                                                                ...keys,
                                                                key,
                                                            ]);
                                                        }}
                                                    >
                                                        {field}
                                                    </ClearsOnChangeLock>
                                                ) : (
                                                    field
                                                )}
                                            </div>
                                        );
                                    }
                                    return null;
                                },
                            )}
                        {/* also render all elements not defined in properties using the schema of patternProperties */}
                        {elementData &&
                            hasAdditionalProperties &&
                            Object.entries(elementData).map(([key, value]) => {
                                const patternKey = findKeyByPatternProperty(key);

                                const currentSchema = patternKey
                                    ? elementSchema.patternProperties[patternKey]
                                    : findSchemaInAdditionalProperties();
                                return (
                                    !elementSchema.properties?.[key] && (
                                        <div key={key} className="formEditorField">
                                            <Accordion
                                                title={
                                                    <PropertyKeyField
                                                        elementData={elementData}
                                                        currentKey={key}
                                                        setData={setData}
                                                        formData={formData}
                                                        dataPath={dataPath}
                                                        findSchema={findSchema}
                                                        elementSchema={elementSchema}
                                                        placeholder={placeholder}
                                                        currentValue={value}
                                                        formSchema={schema}
                                                        currentSchema={currentSchema}
                                                        getAdditionalPropertyLabel={
                                                            additionalPropertyLabel
                                                        }
                                                    />
                                                }
                                                onDelete={
                                                    isReadOnly
                                                        ? undefined
                                                        : () => {
                                                              const newValue = {
                                                                  ...elementData,
                                                              };
                                                              delete newValue[key];
                                                              setData(
                                                                  getUpdatedData(
                                                                      formData,
                                                                      dataPath,
                                                                      newValue,
                                                                  ),
                                                              );
                                                          }
                                                }
                                                isExpandedByDefault
                                                stopTitleClickPropagation
                                            >
                                                <div className="content">
                                                    <SchemaFormFragment
                                                        isRoot={false}
                                                        isReadOnly={isReadOnly}
                                                        isRecordDisabled={isRecordDisabled}
                                                        hideActionButtons={hideActionButtons}
                                                        schema={schema}
                                                        schemaPath={
                                                            patternKey
                                                                ? extendObjectPath(
                                                                      schemaPath,
                                                                      "patternProperties",
                                                                      patternKey,
                                                                  )
                                                                : extendObjectPath(
                                                                      schemaPath,
                                                                      ADDITIONAL_PROPERTIES,
                                                                  )
                                                        }
                                                        formData={formData}
                                                        dataPath={extendObjectPath(dataPath, key)}
                                                        setData={setData}
                                                        errorData={errorData}
                                                        isNewRecord={isNewRecord}
                                                        ajv={ajv}
                                                        omitRequired={omitRequired}
                                                    />
                                                </div>
                                            </Accordion>
                                        </div>
                                    )
                                );
                            })}
                        {/* render all hidden elements defined in properties in a collapsible section.
                            Only const discriminants (form-const-*) are filtered out of the body,
                            so gate the whole "More..." section on whether any hidden property
                            actually renders — otherwise a branch whose only hidden property is the
                            const `type` discriminant would show an empty "More..." section
                            (operator-task-dashboard-widget-editor-form, Margot 2026-09-28 #5). */}
                        {(() => {
                            const renderableHiddenEntries =
                                getRenderableHiddenEntries(hiddenProperties);
                            // A disabled Save must never be a dead end: if the blocking
                            // validation error lives on one of these hidden fields, the
                            // section auto-expands so the inline error becomes visible.
                            const hiddenHasError = renderableHiddenEntries.some(([key]) =>
                                isErrorPathWithin(extendObjectPath(dataPath, key), errorData),
                            );
                            return renderableHiddenEntries.length > 0 ? (
                                <MoreSection hasError={hiddenHasError}>
                                    {renderableHiddenEntries.map(
                                        ([key, value]: [key: string, value: any]) => (
                                            <div key={key} className="formEditorField">
                                                <SchemaFormFragment
                                                    isRoot={false}
                                                    isReadOnly={isReadOnly}
                                                    isRecordDisabled={isRecordDisabled}
                                                    hideActionButtons={hideActionButtons}
                                                    schema={schema}
                                                    schemaPath={extendObjectPath(
                                                        schemaPath,
                                                        "properties",
                                                        key,
                                                    )}
                                                    formData={formData}
                                                    dataPath={extendObjectPath(dataPath, key)}
                                                    setData={setData}
                                                    isRequired={
                                                        !omitRequired &&
                                                        getRequired(elementSchema.required, key)
                                                    }
                                                    isStyleRequired={Boolean(
                                                        getRequired(elementSchema.required, key),
                                                    )}
                                                    errorData={errorData}
                                                    isNewRecord={isNewRecord}
                                                    ajv={ajv}
                                                    omitRequired={omitRequired}
                                                />
                                            </div>
                                        ),
                                    )}
                                </MoreSection>
                            ) : null;
                        })()}
                        {hasAdditionalProperties && !isReadOnly && elementData != null && (
                            <SchemaFormObjectKeyPopover
                                formSchema={schema}
                                elementSchema={elementSchema}
                                formData={formData}
                                elementData={elementData}
                                dataPath={dataPath}
                                setData={setData}
                                isDisabled={isReadOnly ?? false}
                                findSchema={findSchema}
                                additionalPropertyLabel={additionalPropertyLabel()}
                            />
                        )}
                    </LabelledFormElement>
                ) : null;

            case "form-string":
            case "form-optional-string":
                // Read-only display (custom-object list cells, disabled/deleted
                // records, …): render the value as plain text instead of a
                // disabled text input. The interactive <Text> input styles a
                // read-only single-line string as a filled, bordered, rounded
                // box, which reads as a clickable button in list-view cells.
                // Mirrors the read-only branch already used for form-text and
                // form-markdown so single-line and multi-line text attributes
                // render identically as plain values. Secret fields keep the
                // masked input so their value stays hidden.
                if ((isReadOnly || isRecordDisabled) && !elementSchema.secret) {
                    return (
                        <LabelledFormElement {...labelMinimumProps}>
                            <div
                                className="schemaFormReadonlyText"
                                style={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {elementData ?? ""}
                            </div>
                        </LabelledFormElement>
                    );
                }
                return (
                    <LabelledFormElement {...labelProps}>
                        <div className={fieldContainerClassName(errorMessage)}>
                            <Text
                                inputProps={{
                                    onChange: (event) => {
                                        const { value } = event.target;
                                        setData(getUpdatedData(formData, dataPath, value));
                                    },
                                    value: fieldValue ?? "",
                                    isReadOnly: isReadOnly,
                                }}
                                name={`schemaForm.${elementSchema.label}`}
                                attributes={{ placeholder }}
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );

            case "form-duration":
            case "form-optional-duration":
                return (
                    <DurationField
                        errorMessage={errorMessage}
                        smallestUnitValue={elementSchema.smallest_unit}
                        label={elementSchema.label}
                        labelProps={labelProps}
                        setFormData={(duration) =>
                            setData(getUpdatedData(formData, dataPath, duration))
                        }
                        fieldValue={fieldValue}
                        isReadOnly={isReadOnly}
                        actionButtons={actionButtons}
                    />
                );

            // TODO: add variations of form-string when a "format" is specified:
            // format: date
            // format: date-time
            // format: time
            // format: uuid (low)
            // format: timedelta-seconds (low)
            // since JSON does not support those types, they are all returned as strings
            // but we can have nice dedicated input elements

            case "form-datetime":
            case "form-optional-datetime":
            case "form-datetime-with-utcoffset":
            case "form-optional-datetime-with-utcoffset":
            case "form-date":
            case "form-optional-date":
            case "form-time":
            case "form-optional-time":
            case "form-time-with-utcoffset":
            case "form-optional-time-with-utcoffset":
                return (
                    <DatePickerSchemaFormComponent
                        elementSchema={elementSchema}
                        fieldValue={fieldValue}
                        labelProps={labelProps}
                        errorMessage={errorMessage}
                        isReadOnly={isReadOnly}
                        setData={setData}
                        formData={formData}
                        dataPath={dataPath}
                        actionButtons={actionButtons}
                    />
                );

            case "form-text":
            case "form-optional-text":
                // Read-only display (custom-object list cells, disabled/deleted
                // records, …): render the value as plain text instead of mounting a
                // full Monaco code editor. A code editor per read-only cell is both
                // heavy and fragile — for an empty value Monaco can throw during mount
                // ("Illegal argument"), which the fragment error-boundary then surfaced
                // as a raw schema-error string in the object-template CO list grid
                // (reported for the operator-intake `note` column). Mirrors the
                // read-only branch already used for form-markdown above. Secret fields
                // keep the editor so their value stays masked.
                if ((isReadOnly || isRecordDisabled) && !elementSchema.secret) {
                    return (
                        <LabelledFormElement {...labelMinimumProps}>
                            <div
                                className="schemaFormReadonlyText"
                                style={{
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                            >
                                {elementData ?? ""}
                            </div>
                        </LabelledFormElement>
                    );
                }
                return (
                    <LabelledFormElement
                        {...labelProps}
                        isContentButtonAlwaysVisible={elementSchema.secret}
                    >
                        <div className={fieldContainerClassName(errorMessage)}>
                            <ResizeableCodeEditor
                                input={{
                                    name: `schemaForm.${elementSchema.label}`,
                                    value: fieldValue,
                                    onChange: (value) => {
                                        setData(getUpdatedData(formData, dataPath, value));
                                    },
                                    onFocus: () => {},
                                    isReadOnly: isReadOnly,
                                    onBlur: () => {},
                                }}
                                fieldAttr={{}}
                                meta={{}}
                                fieldProps={{
                                    isReadOnly: isReadOnly,
                                    lang: "text",
                                    isNewRecord,
                                    initialHeight: 150,
                                    minHeight: 150,
                                    ...additionalCodeEditorRootProps,
                                }}
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );

            case "form-number":
            case "form-optional-number":
                // Read-only: plain value, like form-string (a disabled number
                // input with action buttons reads as editable). Gated on
                // isReadOnly only, like SchemaFormDropdown: an inline-editable
                // list cell is isRecordDisabled but keeps its live control.
                if (isReadOnly) {
                    return (
                        <LabelledFormElement {...labelMinimumProps}>
                            <div className="schemaFormReadonlyText">{fieldValue ?? ""}</div>
                        </LabelledFormElement>
                    );
                }
                return (
                    <LabelledFormElement {...labelProps}>
                        <div className={fieldContainerClassName(errorMessage)}>
                            <Number
                                name={elementSchema.label}
                                inputProps={{
                                    value: fieldValue,
                                    onChange: (event) => {
                                        const { value } = event.target;
                                        // Clearing the field yields "" → parseFloat("")
                                        // is NaN, which serialises to `.nan` and fails
                                        // validation on a visually empty field. Store
                                        // undefined instead (mirrors form-integer).
                                        const parsed = value === "" ? undefined : parseFloat(value);
                                        setData(getUpdatedData(formData, dataPath, parsed));
                                    },
                                    isReadOnly: isReadOnly,
                                }}
                                // step="any" lets <input type="number"> accept decimals
                                // without the built-in "please enter a valid value"
                                // warning that the default step=1 raises on floats.
                                attributes={{ placeholder, step: "any" }}
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );

            case "form-integer":
            case "form-optional-integer":
                // Read-only: plain value, like form-string (a disabled number
                // input with action buttons reads as editable). Gated on
                // isReadOnly only, like SchemaFormDropdown: an inline-editable
                // list cell is isRecordDisabled but keeps its live control.
                if (isReadOnly) {
                    return (
                        <LabelledFormElement {...labelMinimumProps}>
                            <div className="schemaFormReadonlyText">{fieldValue ?? ""}</div>
                        </LabelledFormElement>
                    );
                }
                return (
                    <LabelledFormElement {...labelProps}>
                        <div className={fieldContainerClassName(errorMessage)}>
                            <Number
                                name={elementSchema.label}
                                inputProps={{
                                    value: fieldValue,
                                    onChange: (event) => {
                                        const { value } = event.target;
                                        // Clearing the field yields "" → parseInt("") is NaN,
                                        // which serialises to `.nan` and fails validation on a
                                        // visually empty field. Store undefined instead.
                                        let parsed = value === "" ? undefined : parseInt(value, 10);
                                        // Clamp to the schema's declared [minimum, maximum]
                                        // range so the form can't store an out-of-range value
                                        // (e.g. a dashboard widget col_span of 0 or negative —
                                        // operator-task-dashboard-widget-editor-form, Margot
                                        // 2026-09-28). Only affects fields that declare a bound;
                                        // unbounded integer fields are unchanged. NaN fails both
                                        // comparisons, so it is left as-is (existing behaviour).
                                        if (parsed !== undefined) {
                                            const { minimum, maximum } = elementSchema;
                                            if (typeof minimum === "number" && parsed < minimum)
                                                parsed = minimum;
                                            if (typeof maximum === "number" && parsed > maximum)
                                                parsed = maximum;
                                        }
                                        setData(getUpdatedData(formData, dataPath, parsed));
                                    },
                                    isReadOnly: isReadOnly,
                                }}
                                attributes={{
                                    placeholder,
                                    min: elementSchema.minimum,
                                    max: elementSchema.maximum,
                                }}
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );

            case "form-const-string":
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        {/* TODO verify correct, https://app.clickup.com/t/861mvr2vq */}
                        {/* {showLabel ? elementSchema.const : null} */}
                    </LabelledFormElement>
                );

            case "form-const-integer":
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        {/* TODO verify correct, https://app.clickup.com/t/861mvr2vq */}
                        {/* {showLabel ? elementSchema.const : null} */}
                    </LabelledFormElement>
                );

            case "form-const-null":
                return <LabelledFormElement {...labelMinimumProps}></LabelledFormElement>;

            case "form-const-boolean":
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        {/* TODO verify correct, https://app.clickup.com/t/861mvr2vq */}
                        {/* {showLabel ? elementSchema.const : null} */}
                    </LabelledFormElement>
                );

            case "form-const-array":
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        {/* TODO verify correct, https://app.clickup.com/t/861mvr2vq */}
                        {/* {showLabel ? elementSchema.const : null} */}
                    </LabelledFormElement>
                );

            case "form-const-object":
                return <LabelledFormElement {...labelMinimumProps} />;

            case "form-boolean":
            case "form-optional-boolean":
                const inputProps = {
                    value: fieldValue,
                    onChange: (event) => {
                        // Toggle the displayed value (fieldValue), not elementData. When the
                        // field is unset but the schema default is true, the switch shows
                        // "true" while elementData is undefined, so `!elementData` made the
                        // first click a visual no-op.
                        setData(getUpdatedData(formData, dataPath, !fieldValue));
                    },
                    isReadOnly: isReadOnly,
                };
                if (elementSchema?.style?.display === "circular") {
                    const handleClick = () => {
                        const isOptional = getIsOptional(elementSchema.element);
                        const isNotRequired = isRequired === false;
                        const nextValue = getNextTriStateValue(
                            elementData,
                            isOptional,
                            isNotRequired,
                        );
                        setData(getUpdatedData(formData, dataPath, nextValue));
                    };
                    return (
                        <div
                            className={`circularSwitchContainer ${
                                elementData === undefined ? "circularSwitchRemoved" : ""
                            }`}
                        >
                            <Tooltip overlay={`${elementSchema.label}: ${elementData}`}>
                                <TriStateCircularSwitch
                                    inputProps={{ ...inputProps, onChange: handleClick }}
                                    label={elementSchema.label}
                                />
                            </Tooltip>
                            {getActionButtons({
                                hasRemoveButton: false,
                            })}
                        </div>
                    );
                } else {
                    return (
                        <LabelledFormElement {...labelProps}>
                            <div className={fieldContainerClassName(errorMessage)}>
                                <TriStateSwitch
                                    identifier={elementSchema.label}
                                    inputProps={inputProps}
                                    fieldProps={{}}
                                />
                                {actionButtons}
                            </div>
                        </LabelledFormElement>
                    );
                }

            case "form-record-filter":
            case "form-optional-record-filter": {
                // Cloudomation record filter built with the advanced-search filter
                // builder (operator-task-fe-dashboard-widget-filter-form-builder).
                // The record type whose columns become filterable fields comes from
                // `record_type` (a constant) or `record_type_field` (the key of a
                // sibling field, e.g. the widget's record_type or relation-graph mode).
                // The stored value is the plain filter JSON, so existing values
                // round-trip unchanged. Read-only views render as form-json (see above).
                const recordTypeField = elementSchema.record_type_field;
                const recordType: string | undefined =
                    elementSchema.record_type ??
                    (recordTypeField
                        ? getByPath({
                              data: formData,
                              dataPath: [...dataPath.slice(0, -1), recordTypeField],
                              schema,
                              elementSchema,
                          })
                        : undefined);
                return (
                    <LabelledFormElement {...labelProps}>
                        <div className={fieldContainerClassName(errorMessage)}>
                            <RecordFilterBuilder
                                name={dataPath.join(".") || "filter"}
                                value={elementData}
                                onChange={(value) =>
                                    setData(getUpdatedData(formData, dataPath, value))
                                }
                                recordType={recordType}
                                missingRecordTypeNote={
                                    recordTypeField
                                        ? `Choose ${String(recordTypeField).replace(/_/g, " ")} first, then build the filter.`
                                        : undefined
                                }
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );
            }
            case "form-json":
                // TODO: this allows arbitrary types & nested structures.
                // It would be nice to have form controls to build & display any json structure.
                return (
                    <LabelledFormElement
                        {...labelProps}
                        isContentButtonAlwaysVisible={elementSchema.secret}
                    >
                        <div className={fieldContainerClassName(errorMessage)}>
                            <ResizeableCodeEditor
                                input={{
                                    name: "schemaFormCodeEditor",
                                    value: serializeStructuredData(elementData, structuredDataMode),
                                    onChange: (value) => {
                                        codeEditorValue.current = value;
                                    },
                                    onFocus: (event) => {},
                                    isReadOnly: isReadOnly,
                                    onBlur: () => {
                                        if (isReadOnly || codeEditorValue.current === undefined)
                                            return;

                                        try {
                                            setData(
                                                getUpdatedData(
                                                    formData,
                                                    dataPath,
                                                    parseStructuredData(
                                                        codeEditorValue.current,
                                                        structuredDataMode,
                                                    ),
                                                ),
                                            );
                                        } catch (error) {
                                            // TODO: improve error handling.
                                            console.warn(
                                                `failed to parse ${structuredDataMode.toUpperCase()}`,
                                                error,
                                            );
                                        }
                                    },
                                }}
                                fieldAttr={{}}
                                meta={{}}
                                fieldProps={{
                                    isReadOnly: isReadOnly,
                                    lang: structuredDataMode,
                                    structuredDataToggle: true,
                                    isNewRecord,
                                    initialHeight: 150,
                                    minHeight: 150,
                                    ...additionalCodeEditorRootProps,
                                }}
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );

            case "form-autocomplete":
            case "form-optional-autocomplete":
                // Read-only display (custom-object list cells, dashboard
                // record-table widgets, disabled/deleted records): render the
                // referenced record as plain linked text with a better empty
                // placeholder, instead of a disabled reference picker that reads
                // as a clickable button and shows "Select an option". Gated on
                // `isReadOnly` only (references are never inline-editable today,
                // so the interactive path is the detail form with
                // isReadOnly=false).
                if (isReadOnly) {
                    return (
                        <LabelledFormElement {...labelMinimumProps}>
                            <SchemaFormReadonlyReference
                                elementSchema={elementSchema}
                                value={elementData}
                                dataPath={dataPath}
                            />
                        </LabelledFormElement>
                    );
                }
                const recordType = elementSchema.record_type?.toLowerCase();
                const isTemplateReference =
                    recordType === customObjectConfig.resourceName &&
                    elementSchema.object_template_id;
                const queryField = isTemplateReference
                    ? {
                          queryField: getSearchObj({
                              field: "object_template_id",
                              op: "eq",
                              value: elementSchema.object_template_id,
                          }),
                      }
                    : {};

                return (
                    <LabelledFormElement {...labelProps}>
                        <div className={fieldContainerClassName(errorMessage)}>
                            <Autocomplete
                                name={""}
                                options={{
                                    resource: recordType,
                                    hasNameAsOptionValue:
                                        elementSchema.store === "record_name_existing" ||
                                        elementSchema.store === "record_name_new",
                                    ...queryField,
                                }}
                                onChange={(value) =>
                                    setData(getUpdatedData(formData, dataPath, value))
                                }
                                value={elementData}
                                isDisabled={isReadOnly}
                                additionalProps={{
                                    clearable: false,
                                    isCreatable: elementSchema.store === "record_name_new",
                                    customKey: JSON.stringify(dataPath),
                                    fieldWidth: "100%",
                                    maxFieldWidth: 420,
                                }}
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );

            case "form-recursion":
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        <SchemaFormFragment
                            isRoot={false}
                            isReadOnly={isReadOnly}
                            isRecordDisabled={isRecordDisabled}
                            hideActionButtons={hideActionButtons}
                            isRequired={isRequired}
                            schema={schema}
                            schemaPath={EMPTY_OBJECT_PATH}
                            formData={formData}
                            dataPath={dataPath}
                            setData={setData}
                            errorData={errorData}
                            isNewRecord={isNewRecord}
                            ajv={ajv}
                            omitRequired={omitRequired}
                        />
                    </LabelledFormElement>
                );

            case "form-progress-bar":
            case "form-optional-progress-bar":
                if (elementData == null && elementSchema.element === "form-optional-progress-bar") {
                    return (
                        <LabelledFormElement {...labelProps}>
                            <ProgressBarSchemaFormComponent value={null} />
                        </LabelledFormElement>
                    );
                }
                return (
                    <LabelledFormElement {...labelProps}>
                        <ProgressBarSchemaFormComponent value={fieldValue} />
                    </LabelledFormElement>
                );

            case "form-markdown":
            case "form-optional-markdown":
                // Read-only: keep the rich rendered markdown (with clickable
                // record links via CustomLinkMarkdown). An empty optional
                // markdown has nothing to show, so skip rendering it entirely.
                if (isReadOnly || isRecordDisabled) {
                    if (elementData == null && elementSchema.element === "form-optional-markdown") {
                        return null;
                    }
                    return (
                        <LabelledFormElement {...labelMinimumProps}>
                            <div>
                                <CustomLinkMarkdown
                                    markdownString={elementData ?? ""}
                                    className="statusMessageMarkdown"
                                />
                            </div>
                        </LabelledFormElement>
                    );
                }
                // Editable: always render an (empty) editable field, even when
                // the value is null/"", so the field is visible and clickable.
                // Previously an unset optional markdown (e.g. an unanswered
                // operator-question `answer`) rendered nothing at all, so there
                // was no field to click into to type a value.
                // Editable: render the value as markdown but let the user click
                // to edit it in place (textarea, commit on blur). Previously
                // MARKDOWN attributes were read-only in the form view and could
                // only be changed via the raw JSON/code view.
                return (
                    <LabelledFormElement {...labelProps}>
                        <div className={fieldContainerClassName(errorMessage)}>
                            <EditableMarkdownComponent
                                identifier={`schemaForm.${elementSchema.label}`}
                                inputProps={{
                                    value: elementData ?? "",
                                    // EditableMarkdownComponent emits the new
                                    // string value (the Monaco markdown editor
                                    // calls onChange with the text, not a DOM
                                    // event) — consume it directly, like every
                                    // other element here. Reading `event.target
                                    // .value` off a string silently dropped the
                                    // edit, so answers never persisted.
                                    onChange: (value) =>
                                        setData(getUpdatedData(formData, dataPath, value)),
                                    onFocus: () => {},
                                    onBlur: () => {},
                                    isReadOnly: false,
                                }}
                                attributes={{ placeholder }}
                                isReadOnly={false}
                                isCollapsible={false}
                                // The heavier bordered/resizable CodeEditor is
                                // used only in a full open-record schema form.
                                // The compact popover contexts (table-cell
                                // markdown editing, inline description editing)
                                // set hideActionButtons, and there the lighter
                                // markdown editor is used instead — the heavy
                                // code-editor chrome was oversized/out-of-place
                                // there (Margot, 2026-08-18).
                                useCodeEditor={!hideActionButtons}
                            />
                            {actionButtons}
                        </div>
                    </LabelledFormElement>
                );

            case "form-color-picker":
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        <ColorPicker
                            color={elementData}
                            onChange={(value) => setData(getUpdatedData(formData, dataPath, value))}
                            isReadOnly={isReadOnly || isRecordDisabled}
                        />
                    </LabelledFormElement>
                );

            case "form-submit": {
                // Schema-form-dialect action button (operator-task-schema-form-add-
                // form-submit-action-element). The property NAME is the action
                // identifier: clicking writes {<name>: true} into the form data and
                // calls onSubmitAction(name) so the surrounding chrome (e.g. the
                // interactive message screen) submits the response. Multiple
                // form-submit leaves render as multiple buttons; the pressed one's
                // key is true in the response — identical dispatch to the legacy
                // `submit` message field ("which key is present/true in response").
                const actionName =
                    Array.isArray(dataPath) && dataPath.length > 0
                        ? String(dataPath[dataPath.length - 1])
                        : (elementSchema.label ?? "");
                // Button role per ui-design-principle-buttons: equal-peer action
                // buttons are `dark`; the flow author opts the single dominant one
                // into `primary` via the element's `variation`. Default `dark` so
                // multiple buttons never violate the "<=1 primary per view" rule.
                const buttonVariation = elementSchema.variation ?? "dark";
                return (
                    <div className="schemaFormSubmitButton">
                        <Button
                            label={elementSchema.label || actionName}
                            variation={buttonVariation}
                            loading={pendingSubmitAction === actionName}
                            disabled={
                                isReadOnly ||
                                isRecordDisabled ||
                                !onSubmitAction ||
                                (pendingSubmitAction ?? null) !== null
                            }
                            onClick={() => {
                                setData(getUpdatedData(formData, dataPath, true));
                                onSubmitAction?.(actionName);
                            }}
                        />
                    </div>
                );
            }

            case "form-incoming-reference":
                // Inline reverse-reference element (operator-task-incoming-
                // reference-schema-form-table-element). Lists the records that
                // reference the CURRENT record through the one relation this
                // attribute declares. The current record's identity is published
                // by the SchemaForm root via SchemaFormRecordContext and read
                // inside IncomingReference, so it is not threaded through the
                // fragment tree. Read-only in Phase 2 (backend emits read_only).
                return (
                    // The incoming-reference element is a virtual display/interactive
                    // element that is ALWAYS present: it has no stored value, so
                    // `elementData` is undefined and the backend excludes it from
                    // `required` — which would make labelProps flag it `isRemoved`
                    // and render it in the grayed-out, half-opacity "click to add"
                    // state (that state also sets `pointer-events: none`, which would
                    // disable the referrer row links and any Phase-3 add/remove
                    // controls). Force it never-removed.
                    // (operator-task-incoming-reference-schema-form-table-element)
                    <LabelledFormElement {...labelProps} isRemoved={false}>
                        <IncomingReference node={elementSchema} />
                    </LabelledFormElement>
                );

            default:
                if (isRoot) {
                    return (
                        <Callout
                            text={SchemaFormRenderErrors.unknownFormElement.message}
                            type={"warning"}
                        />
                    );
                }
                return (
                    <LabelledFormElement {...labelMinimumProps}>
                        <Callout
                            text={`Unknown form element: ${elementSchema.element}`}
                            type={"warning"}
                        />
                    </LabelledFormElement>
                );
        }
    }, [
        elementSchema,
        rememberedCollapse,
        labelMinimumProps,
        isReadOnly,
        unlockedKeys,
        isRecordDisabled,
        schema,
        formData,
        dataPath,
        setData,
        errorData,
        isNewRecord,
        ajv,
        elementData,
        schemaPath,
        isRoot,
        showLabel,
        isRequired,
        styleRequired,
        shownButtons,
        actionButtons,
        hideActionButtons,
        labelProps,
        shouldRender,
        errorMessage,
        fieldValue,
        placeholder,
        additionalCodeEditorRootProps,
        findKeyByPatternProperty,
        findSchemaInAdditionalProperties,
        omitRequired,
        getRequired,
        getActionButtons,
        arrayItemIds,
        structuredDataMode,
        onSubmitAction,
    pendingSubmitAction,
    ]);

    return <>{memoizedElement}</>;
};

const areSchemaFormFragmentPropsEqual = (
    prev: SchemaFormFragmentProps,
    next: SchemaFormFragmentProps,
): boolean =>
    prev.schema === next.schema &&
    prev.schemaPath === next.schemaPath &&
    prev.formData === next.formData &&
    prev.dataPath === next.dataPath &&
    prev.setData === next.setData &&
    prev.defaultOverridePath === next.defaultOverridePath &&
    prev.isRoot === next.isRoot &&
    prev.isReadOnly === next.isReadOnly &&
    prev.showLabel === next.showLabel &&
    prev.isRequired === next.isRequired &&
    prev.isStyleRequired === next.isStyleRequired &&
    prev.isRecordDisabled === next.isRecordDisabled &&
    prev.hideActionButtons === next.hideActionButtons &&
    prev.errorData === next.errorData &&
    prev.isNewRecord === next.isNewRecord &&
    prev.ajv === next.ajv &&
    prev.additionalCodeEditorRootProps === next.additionalCodeEditorRootProps &&
    prev.omitRequired === next.omitRequired &&
    prev.onSubmitAction === next.onSubmitAction &&
    prev.pendingSubmitAction === next.pendingSubmitAction;

const MemoizedSchemaFormFragment = memo(
    SchemaFormFragmentWithError,
    areSchemaFormFragmentPropsEqual,
);

const SchemaFormFragment = withErrorBoundary(MemoizedSchemaFormFragment, {
    fallbackRender: ({ error }) => (
        <Callout
            text={
                <>
                    {`${error?.message}.`}{" "}
                    <a
                        href={getDocumentationUrl(
                            "/docs/schema-creation-and-validation#input-and-output-schema",
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        See documentation
                    </a>{" "}
                    on how to set up a schema.
                </>
            }
            type={"warning"}
            isClosable
            className="rootCallout"
        />
    ),
    onError(error) {
        console.error(error);
    },
});

export { SchemaFormFragment };
